// Petit kit UI partagé — design moderne (Tailwind)
import React from 'react';

export const PageHeader = ({ title, subtitle, children }) => (
  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
    <div>
      <h1 className="text-2xl font-bold text-slate-800">{title}</h1>
      {subtitle && <p className="text-sm text-slate-500 mt-1">{subtitle}</p>}
    </div>
    {children && <div className="flex items-center gap-2">{children}</div>}
  </div>
);

export const Card = ({ children, className = "" }) => (
  <div className={`bg-white rounded-xl shadow-sm border border-slate-200 ${className}`}>
    {children}
  </div>
);

export const Badge = ({ children, color = "slate" }) => {
  const colors = {
    slate: "bg-slate-100 text-slate-700",
    green: "bg-emerald-100 text-emerald-700",
    red: "bg-rose-100 text-rose-700",
    amber: "bg-amber-100 text-amber-700",
    indigo: "bg-indigo-100 text-indigo-700",
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${colors[color] || colors.slate}`}>
      {children}
    </span>
  );
};

export const statusColor = (status) => {
  if (!status) return "slate";
  if (status.includes("ok") || status.includes("success")) return "green";
  if (status.includes("error") || status.includes("fail")) return "red";
  return "amber";
};

export const Spinner = () => (
  <div className="flex items-center justify-center py-12">
    <div className="w-8 h-8 border-4 border-indigo-200 border-t-indigo-600 rounded-full animate-spin"></div>
  </div>
);

export const Input = (props) => (
  <input
    {...props}
    className={`border border-slate-300 rounded-lg px-3 py-2 text-sm text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 ${props.className || ""}`}
  />
);

export const Select = (props) => (
  <select
    {...props}
    className={`border border-slate-300 rounded-lg px-3 py-2 text-sm text-slate-700 bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 ${props.className || ""}`}
  />
);

export const Button = ({ children, variant = "primary", className = "", ...props }) => {
  const variants = {
    primary: "bg-indigo-600 hover:bg-indigo-700 text-white",
    danger: "bg-rose-600 hover:bg-rose-700 text-white",
    ghost: "bg-white hover:bg-slate-50 text-slate-700 border border-slate-300",
    success: "bg-emerald-600 hover:bg-emerald-700 text-white",
  };
  return (
    <button
      {...props}
      className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${variants[variant]} ${className}`}
    >
      {children}
    </button>
  );
};

export const Pagination = ({ total, limit, offset, onPage }) => {
  const page = Math.floor(offset / limit) + 1;
  const pages = Math.max(Math.ceil(total / limit), 1);
  if (pages <= 1) return <div className="text-sm text-slate-500 px-1">{total} résultat{total > 1 ? "s" : ""}</div>;
  return (
    <div className="flex items-center justify-between flex-wrap gap-2">
      <div className="text-sm text-slate-500">
        {total} résultat{total > 1 ? "s" : ""} — page {page} / {pages}
      </div>
      <div className="flex items-center gap-1">
        <Button variant="ghost" disabled={page <= 1} onClick={() => onPage(0)}>«</Button>
        <Button variant="ghost" disabled={page <= 1} onClick={() => onPage(offset - limit)}>‹ Préc.</Button>
        <Button variant="ghost" disabled={page >= pages} onClick={() => onPage(offset + limit)}>Suiv. ›</Button>
        <Button variant="ghost" disabled={page >= pages} onClick={() => onPage((pages - 1) * limit)}>»</Button>
      </div>
    </div>
  );
};

export const StatCard = ({ label, value, icon, accent = "indigo" }) => {
  const accents = {
    indigo: "from-indigo-500 to-indigo-600",
    emerald: "from-emerald-500 to-emerald-600",
    amber: "from-amber-500 to-amber-600",
    rose: "from-rose-500 to-rose-600",
  };
  return (
    <Card className="p-5 flex items-center gap-4">
      <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${accents[accent]} flex items-center justify-center text-white text-xl shrink-0`}>
        {icon}
      </div>
      <div className="min-w-0">
        <div className="text-2xl font-bold text-slate-800">{Number(value ?? 0).toLocaleString("fr-FR")}</div>
        <div className="text-sm text-slate-500 truncate">{label}</div>
      </div>
    </Card>
  );
};

export const formatDate = (d) => {
  if (!d) return "—";
  return new Date(d).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" });
};
